import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'label',
    'collapsedLabel',
    'toggleLabel',
    'icon',
    'search',
    'searchInputWrapper',
    'searchButtonWrapper',
    'avatar',
    'avatarWrapper',
    'navItem',
    'toggleButton',
    'subNav',
    'arrowButton',
    'arrowIcon',
  ];
  static classes = ['collapsed'];

  connect() {
    this.hoverTimeout = null;
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleScroll = this.handleScroll.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    window.addEventListener('scroll', this.boundHandleScroll, true);

    // Collapsed state is rendered server-side (see Nova::Navigation::SidebarComponent#collapsed?)
    // so the DOM already reflects the right state before this controller connects.
    this.collapsed = this.element.classList.contains('is-collapsed');

    this.updateCollapseIconRotation();
    this.initializeSubNavStates();

    // Track modal state to prevent subNav hiding during modal interactions
    this.modalOpen = false;
    this._onModalOpen = () => { this.modalOpen = true; };
    this._onModalClose = () => { this.modalOpen = false; };
    document.addEventListener('modal-manager:open', this._onModalOpen);
    document.addEventListener('modal-manager:close', this._onModalClose);

    // Track cursor position for accurate hover detection
    this.cursorX = 0;
    this.cursorY = 0;
    this.boundTrackCursor = this.trackCursor.bind(this);
    document.addEventListener('mousemove', this.boundTrackCursor);

    // Initialize tooltip
    this.tooltip = null;
    this.setupTooltipListeners();
  }

  trackCursor(event) {
    this.cursorX = event.clientX;
    this.cursorY = event.clientY;
  }

  initializeSubNavStates() {
    // Don't set maxHeight when collapsed - subNavs work as flyouts in that mode
    if (this.collapsed) return;

    // Initialize subNav maxHeight based on data-expanded attribute
    this.subNavTargets.forEach((subNav) => {
      const mainItem = subNav.closest('[data-sidebar-target="mainItem"]');
      const isExpanded = mainItem?.dataset.expanded === 'true';

      if (isExpanded) {
        subNav.style.maxHeight = `${subNav.scrollHeight}px`;
      } else {
        subNav.style.maxHeight = '0px';
      }
    });
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    window.removeEventListener('scroll', this.boundHandleScroll, true);
    document.removeEventListener('mousemove', this.boundTrackCursor);
    clearTimeout(this.hoverTimeout);

    // Clean up modal event listeners
    document.removeEventListener('modal-manager:open', this._onModalOpen);
    document.removeEventListener('modal-manager:close', this._onModalClose);
    this._onModalOpen = null;
    this._onModalClose = null;

    // Clean up tooltip
    this.removeTooltip();
    this.removeTooltipListeners();

    // Clean up all inline styles and classes
    this.subNavTargets.forEach((subNav) => {
      subNav.style.maxHeight = '';
      subNav.classList.remove('hidden', 'opacity-0', 'opacity-100', 'pointer-events-none', 'pointer-events-auto', 'sub-nav-visible');

      const mainItem = subNav.closest('[data-sidebar-target="mainItem"]');
      if (mainItem) {
        mainItem.removeAttribute('data-expanded');
      }

      const arrowIcon = mainItem?.querySelector('[data-sidebar-target="arrowIcon"]');
      if (arrowIcon) {
        arrowIcon.classList.remove('rotate-180');
      }
    });

    // Clean up collapse icon
    const iconEl = this.hasToggleButtonTarget
      ? this.toggleButtonTarget.querySelector('[data-sidebar-target="icon"]')
      : this.iconTargets?.[0];

    if (iconEl) {
      iconEl.classList.remove('rotate-180');
    }
  }

  toggle(event) {
    this.collapsed = !this.collapsed;
    this.element.classList.toggle(this.collapsedClass);
    this.saveState();

    if (this.collapsed) {
      this.element.classList.add('sidebar', 'is-collapsed');
      this.subNavTargets.forEach((subNav) => {
        subNav.style.maxHeight = '';
        this.hideSubNav(subNav);

        const mainItem = subNav.closest('[data-sidebar-target="mainItem"]');
        if (mainItem && mainItem.hasAttribute('data-expanded')) {
          mainItem.removeAttribute('data-expanded');
        }

        const arrowIcon = mainItem?.querySelector('[data-sidebar-target="arrowIcon"]');
        if (arrowIcon) {
          arrowIcon.classList.remove('rotate-180');
        }
      });
    } else {
      this.element.classList.remove('sidebar', 'is-collapsed');
      this.subNavTargets.forEach((subNav) => {
        subNav.classList.remove('hidden', 'opacity-0', 'pointer-events-none');
      });
    }

    event?.currentTarget?.blur();

    this.labelTargets.forEach((label) => {
      label.classList.toggle('hidden', this.collapsed);
    });

    this.arrowIconTargets.forEach((arrowIcon) => {
      arrowIcon.classList.toggle('hidden', this.collapsed);
    });

    this.collapsedLabelTargets.forEach((label) => {
      label.classList.toggle('hidden', !this.collapsed);
    });

    if (this.hasSearchTarget) {
      this.searchTarget.classList.toggle('w-full', !this.collapsed);
      this.searchTarget.classList.toggle('w-[38px]', this.collapsed);
      this.searchTarget.classList.toggle('pl-[40px]', !this.collapsed);
      this.searchTarget.classList.toggle('pl-0', this.collapsed);

      if (this.collapsed) {
        this._originalPlaceholder = this.searchTarget.placeholder;
        this.searchTarget.placeholder = '';
      } else if (this._originalPlaceholder) {
        this.searchTarget.placeholder = this._originalPlaceholder;
      }
    }

    if (this.hasSearchInputWrapperTarget && this.hasSearchButtonWrapperTarget) {
      if (this.collapsed) {
        this.searchInputWrapperTarget.classList.add('hidden');
        this.searchButtonWrapperTarget.classList.remove('hidden');
      } else {
        this.searchInputWrapperTarget.classList.remove('hidden');
        this.searchButtonWrapperTarget.classList.add('hidden');
      }
    }

    this.navItemTargets.forEach((item) => {
      item.classList.toggle('w-auto', !this.collapsed);
      item.classList.toggle('w-[40px]', this.collapsed);
      item.classList.toggle('justify-start', !this.collapsed);
      item.classList.toggle('justify-center', this.collapsed);
      item.classList.toggle('mx-auto', this.collapsed);
    });

    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.classList.toggle('w-auto', !this.collapsed);
      this.toggleButtonTarget.classList.toggle('w-[40px]', this.collapsed);
      this.toggleButtonTarget.classList.toggle('mx-auto', this.collapsed);
      this.toggleButtonTarget.setAttribute('aria-expanded', String(!this.collapsed));
    }

    if (this.hasToggleLabelTarget) {
      this.toggleLabelTarget.textContent = this.collapsed ? 'Expand Sidebar' : 'Collapse Sidebar';
    }

    this.updateCollapseIconRotation();
  }

  updateCollapseIconRotation() {
    const iconEl = this.hasToggleButtonTarget
      ? this.toggleButtonTarget.querySelector('[data-sidebar-target="icon"]')
      : this.iconTargets?.[0];

    if (!iconEl) return;

    iconEl.classList.toggle('rotate-180', !this.collapsed);
  }

  expandForSearch(event) {
    if (this.collapsed) {
      this.toggle(event);

      setTimeout(() => {
        const searchInput = this.element.querySelector('[data-search-results-target="input"]');
        if (searchInput) {
          searchInput.focus();
        }
      }, 350);
    }
  }

  toggleSubNav(event) {
    if (this.collapsed) {
      // Blur to remove focus ring on Windows browsers
      event.currentTarget?.blur();
      return;
    }

    const link = event.currentTarget;
    const mainItem = link.closest('[data-sidebar-target="mainItem"]');
    const subNav = mainItem.querySelector('[data-sidebar-target="subNav"]');
    const arrowIcon = mainItem.querySelector('[data-sidebar-target="arrowIcon"]');

    if (!subNav) return;

    event.preventDefault();

    const isExpanded = mainItem.dataset.expanded === 'true';

    if (isExpanded) {
      this.collapseSubNav(subNav, mainItem, arrowIcon);
    } else {
      this.expandSubNav(subNav, mainItem, arrowIcon);
    }
  }

  onMainItemEnter(event) {
    clearTimeout(this.hoverTimeout);

    if (!this.collapsed) return;

    this.subNavTargets.forEach((subNav) => {
      this.hideSubNav(subNav);
    });

    const subNav = event.currentTarget.querySelector('[data-sidebar-target="subNav"]');
    if (subNav) {
      this.showSubNav(subNav);
    }
  }

  onSubNavEnter() {
    clearTimeout(this.hoverTimeout);
  }

  onSubNavLeave(event) {
    const subNav = event.currentTarget;
    if (!this.collapsed) return;

    this.hoverTimeout = setTimeout(() => {
      if (!this.isCursorInsideSidebarOrSubNav()) {
        this.hideSubNav(subNav);
      }
    }, 300);
  }

  onSidebarLeave() {
    if (!this.collapsed) return;

    this.subNavTargets.forEach((subNav) => {
      this.hideSubNav(subNav);
    });
  }

  onMainItemLeave(event) {
    if (!this.collapsed) return;

    this.hoverTimeout = setTimeout(() => {
      if (!this.isCursorInsideSidebarOrSubNav()) {
        this.subNavTargets.forEach((subNav) => {
          this.hideSubNav(subNav);
        });
      }
    }, 300);
  }

  handleClickOutside(event) {
    // Don't hide subNav when modal is open - user might be interacting with modal
    if (this._isAnyModalOpen()) return;

    if (!this.element.contains(event.target)) {
      this.subNavTargets.forEach((subNav) => {
        this.hideSubNav(subNav);
      });
    }
  }

  handleScroll() {
    if (this.collapsed) {
      this.subNavTargets.forEach((subNav) => {
        this.hideSubNav(subNav);
      });
    }
  }

  isCursorInsideSidebarOrSubNav() {
    const { cursorX, cursorY } = this;

    // Check if cursor is inside sidebar
    const sidebarRect = this.element.getBoundingClientRect();
    const insideSidebar =
      cursorX >= sidebarRect.left &&
      cursorX <= sidebarRect.right &&
      cursorY >= sidebarRect.top &&
      cursorY <= sidebarRect.bottom;

    if (insideSidebar) return true;

    // When collapsed, also check if cursor is inside any visible subNav tooltip
    if (this.collapsed) {
      for (const subNav of this.subNavTargets) {
        if (!subNav.classList.contains('sub-nav-visible')) continue;
        const subNavRect = subNav.getBoundingClientRect();
        if (
          cursorX >= subNavRect.left &&
          cursorX <= subNavRect.right &&
          cursorY >= subNavRect.top &&
          cursorY <= subNavRect.bottom
        ) {
          return true;
        }
      }
    }

    return false;
  }

  // Helper method to detect if any modal is currently open
  _isAnyModalOpen() {
    // Check event-based flag first (fastest)
    if (this.modalOpen) return true;

    // Fallback: Check DOM for visible modals (supports all 3 modal systems)
    const novaAppModals = document.querySelectorAll('[data-modal-manager-target="modal"]:not(.hidden)');
    if (novaAppModals.length > 0) return true;

    // 2. Nova legacy modals
    const novaLegacyModals = document.querySelectorAll('main > .modal:not(.hidden)');
    if (novaLegacyModals.length > 0) return true;

    return false;
  }

  // Helper methods for managing subNav state with Tailwind classes
  hideSubNav(subNav) {
    subNav.classList.add('hidden', 'opacity-0', 'pointer-events-none');
    subNav.classList.remove('opacity-100', 'pointer-events-auto', 'sub-nav-visible');
  }

  showSubNav(subNav) {
    subNav.classList.remove('hidden', 'opacity-0', 'pointer-events-none');
    subNav.classList.add('opacity-100', 'pointer-events-auto', 'sub-nav-visible');
  }

  collapseSubNav(subNav, mainItem, arrowIcon) {
    subNav.style.maxHeight = '0px';
    mainItem?.removeAttribute('data-expanded');
    arrowIcon?.classList.remove('rotate-180');
  }

  expandSubNav(subNav, mainItem, arrowIcon) {
    // CRITICAL: Remove hiding classes added by hideSubNav()
    subNav.classList.remove('hidden', 'opacity-0', 'pointer-events-none');
    subNav.classList.add('opacity-100', 'pointer-events-auto');

    // Use scrollHeight for smooth animation
    const scrollHeight = subNav.scrollHeight;
    subNav.style.maxHeight = `${scrollHeight}px`;
    mainItem?.setAttribute('data-expanded', 'true');
    arrowIcon?.classList.add('rotate-180');
  }

  setupTooltipListeners() {
    this.boundShowTooltip = this.showTooltip.bind(this);
    this.boundHideTooltip = this.hideTooltip.bind(this);

    this.labelTargets.forEach((label) => {
      const parent = label.closest('[class*="flex"][class*="items-center"]');
      const target = parent || label;

      // Skip elements with sub_items (they have subNav inside mainItem)
      const mainItem = target.closest('[data-sidebar-target="mainItem"]');
      if (mainItem?.querySelector('[data-sidebar-target="subNav"]')) return;

      target.addEventListener('mouseenter', this.boundShowTooltip);
      target.addEventListener('mouseleave', this.boundHideTooltip);
    });
  }

  removeTooltipListeners() {
    this.labelTargets.forEach((label) => {
      const parent = label.closest('[class*="flex"][class*="items-center"]');
      const target = parent || label;

      target.removeEventListener('mouseenter', this.boundShowTooltip);
      target.removeEventListener('mouseleave', this.boundHideTooltip);
    });
  }

  showTooltip(event) {
    if (!this.collapsed) return;

    const target = event.currentTarget;
    const label = target.querySelector('[data-sidebar-target="label"]') || target;
    const text = label.textContent?.trim();

    if (!text) return;

    this.removeTooltip();

    this.tooltip = document.createElement('div');
    this.tooltip.textContent = text;
    this.tooltip.className = 'fixed z-50 px-12 py-8 text-paragraph-s font-medium text-black-1000 bg-white rounded-8 shadow-lg whitespace-nowrap pointer-events-none';

    document.body.appendChild(this.tooltip);

    const sidebarRect = this.element.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();

    this.tooltip.style.left = `${sidebarRect.right - 12}px`;
    this.tooltip.style.top = `${targetRect.top + (targetRect.height - tooltipRect.height) / 2}px`;
  }

  hideTooltip() {
    this.removeTooltip();
  }

  removeTooltip() {
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
    }
  }

  // === Cookie persistence (read server-side by Nova::Navigation::SidebarComponent) ===
  saveState() {
    document.cookie = `sidebar_collapsed=${this.collapsed}; path=/; max-age=31536000; SameSite=Lax`;
  }
}
